<template>
    <div class="voucher-modal-backdrop" @click.self="handleClose">
        <div class="voucher-modal-card" ref="modalCard">
            <div class="voucher-hero" :style="heroStyle">
                <button class="voucher-modal-close" type="button" @click="handleClose">&times;</button>
                <span v-if="pointDisplay" class="voucher-point-chip">{{ pointDisplay }}</span>
            </div>

            <div class="voucher-content">
                <div class="voucher-header">
                    <h2 class="voucher-title">{{ title }}</h2>
                    <p class="voucher-merchant">{{ merchantInfo }}</p>
                </div>

                <div class="voucher-divider dashed"></div>

                <div class="voucher-meta-row">
                    <div v-if="validUntil" class="meta-col">
                        <i class="far fa-calendar-alt meta-icon"></i>
                        <div class="meta-text">
                            <span class="meta-label">Valid until</span>
                            <span class="meta-value">{{ validUntil }}</span>
                        </div>
                    </div>
                    <div class="meta-col" v-if="pointDisplay">
                        <i class="fas fa-coins meta-icon"></i>
                        <div class="meta-text">
                            <span class="meta-label">Requires</span>
                            <span class="meta-value">{{ pointDisplay }}</span>
                        </div>
                    </div>
                    <div class="meta-col" v-if="voucherTypeDisplay">
                        <i class="fas fa-tag meta-icon"></i>
                        <div class="meta-text">
                            <span class="meta-label">Voucher Type</span>
                            <span class="meta-value">{{ voucherTypeDisplay }}</span>
                        </div>
                    </div>
                    <div class="meta-col">
                        <i class="fas fa-undo-alt meta-icon"></i>
                        <div class="meta-text">
                            <span class="meta-label">Cancelable</span>
                            <span class="meta-value">{{ cancelableDisplay }}</span>
                        </div>
                    </div>
                </div>

                <div class="voucher-desc-section">
                    <div class="desc-header" @click="toggleDescription">
                        <span class="desc-title">Description</span>
                        <button class="desc-toggle-btn" type="button">
                            <i class="fas" :class="isDescriptionExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                        </button>
                    </div>
                    
                    <div class="desc-content-wrapper" :class="{ 'is-expanded': isDescriptionExpanded }">
                        <div class="voucher-description-html" v-html="description" v-if="isDescriptionExpanded"></div>
                        <div class="voucher-description-snippet" v-else>{{ truncatedDescription }}</div>
                    </div>
                </div>

                <!-- Claimable Locations Section -->
                <div class="voucher-eligible-section" v-if="eligibleMerchantsList.length > 0">
                    <div class="eligible-header">
                        <span class="eligible-title">
                            <i class="fas fa-store eligible-title-icon"></i> Claimable Locations
                        </span>
                        <span class="eligible-count-badge">
                            {{ isAllCorporate ? 'All Merchants' : `${eligibleMerchantsList.length} Locations` }}
                        </span>
                    </div>
                    
                    <div class="eligible-list-container">
                        <div 
                            v-for="(item, idx) in eligibleMerchantsList" 
                            :key="idx" 
                            class="eligible-merchant-item"
                            :class="{ 'is-last': idx === eligibleMerchantsList.length - 1 }"
                        >
                            <div class="merchant-info-group">
                                <i class="fas fa-map-marker-alt location-marker-icon"></i>
                                <div>
                                    <div class="merchant-name">{{ item.merchant_name }}</div>
                                    <div class="branch-name">{{ item.branch_name }}</div>
                                </div>
                            </div>
                            <span class="valid-status-badge">
                                Valid
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Smooth White Gradient & Text-Only Scroll Hint -->
            <div 
                v-if="showScrollHint" 
                class="scroll-hint-overlay"
            >
                <div class="scroll-hint-text" @click="scrollToBottom" title="Scroll down for details">
                    <span>Scroll to see details</span>
                    <i class="fas fa-chevron-down scroll-hint-icon"></i>
                </div>
            </div>

            <div class="voucher-footer">
                <button
                    type="button"
                    class="voucher-redeem"
                    :class="{ 'is-redeeming': isRedeeming, 'is-disabled': disabled || !isOnline }"
                    @click="handleRedeem"
                    :disabled="isRedeeming || disabled || !isOnline"
                >
                    <span class="voucher-redeem-label">{{ !isOnline ? 'Offline - Cannot Redeem' : (disabled ? 'Voucher Unavailable' : 'Redeem voucher') }}</span>
                    <span class="voucher-redeem-rip voucher-redeem-rip--left"></span>
                    <span class="voucher-redeem-rip voucher-redeem-rip--right"></span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import dayjs from 'dayjs'

export default {
    name: 'VoucherDetailModal',
    props: {
        voucher: {
            type: Object,
            required: true,
            default: () => ({})
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    emits: ['close', 'redeem'],
    mounted() {
        document.body.style.overflow = 'hidden';
        this.$nextTick(() => {
            this.checkScroll();
            if (this.$refs.modalCard) {
                this.$refs.modalCard.addEventListener('scroll', this.checkScroll);
            }
        });
    },
    beforeUnmount() {
        document.body.style.overflow = '';
        if (this.$refs.modalCard) {
            this.$refs.modalCard.removeEventListener('scroll', this.checkScroll);
        }
    },
    data() {
        return {
            isRedeeming: false,
            redeemTimeout: null,
            isDescriptionExpanded: false,
            showScrollHint: false
        };
    },
    computed: {
        title() {
            return this.voucher?.name || 'Membership Voucher';
        },
        isAllCorporate() {
            return this.voucher?.merchant_scope === 'all_corporate';
        },
        eligibleMerchantsList() {
            if (Array.isArray(this.voucher?.eligible_merchants_info) && this.voucher.eligible_merchants_info.length > 0) {
                return this.voucher.eligible_merchants_info;
            }
            if (Array.isArray(this.voucher?.allowed_merchants) && this.voucher.allowed_merchants.length > 0) {
                return this.voucher.allowed_merchants.map(m => {
                    if (typeof m === 'object' && m !== null) {
                        return {
                            merchant_name: m.name || m.text || m.merchant_name || 'Merchant',
                            branch_name: m.branch?.name || m.branch_name || 'General Branch'
                        };
                    }
                    return {
                        merchant_name: m,
                        branch_name: 'General Branch'
                    };
                });
            }
            const merchantName = this.voucher?.merchant?.name || this.voucher?.merchant_name;
            const branchName = this.voucher?.merchant?.branch?.name || this.voucher?.branch?.name || this.voucher?.branch_name;
            if (merchantName) {
                return [{
                    merchant_name: merchantName,
                    branch_name: branchName || 'General Branch'
                }];
            }
            return [];
        },
        merchantInfo() {
            if (this.isAllCorporate) {
                return 'Claimable at All Merchants & Branches';
            }
            const count = this.eligibleMerchantsList.length;
            if (count > 1) {
                return `Claimable at ${count} Locations`;
            }
            const merchant = this.voucher?.provider_name || this.voucher?.corporate_name || this.voucher?.merchant_name || this.voucher?.merchant?.name;
            const branch = this.voucher?.branch_name || this.voucher?.merchant?.branch?.name || this.voucher?.branch?.name;
            if (merchant && branch) {
                return `${merchant} • ${branch}`;
            }
            return merchant || branch || '—';
        },
        description() {
            return this.voucher?.desc
                || this.voucher?.description
                || 'Redeem this voucher to enjoy exclusive member privileges.';
        },
        truncatedDescription() {
            if (!this.description) return '';
            const stripped = this.description.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
            if (stripped.length <= 30) return stripped;
            return stripped.substring(0, 30) + '...';
        },
        pointDisplay() {
            const points = this.voucher?.point || this.voucher?.points;
            if (points === undefined || points === null) return null;
            return `${points} POINT` + (Number(points) === 1 ? '' : 'S');
        },
        voucherTypeDisplay() {
            const type = this.voucher?.voucher_type;
            if (type === 'product_service') return 'Product & Service';
            if (type === 'credit_discount') return 'Discount Credit';
            return 'Voucher';
        },
        cancelableDisplay() {
            const cancelable = this.voucher?.cancelable;
            return (cancelable === 1 || cancelable === true || cancelable === '1') ? 'Yes' : 'No';
        },
        discountDisplay() {
            if (this.voucher?.voucher_type !== 'credit_discount') {
                return this.title;
            }
            const type = this.voucher?.discount_type;
            const val = this.voucher?.discount_value;
            if (!val) return 'Discount';
            if (type === 'percentage') {
                return `${val}% Discount`;
            } else {
                return `Rp ${Number(val).toLocaleString('id-ID')} Discount`;
            }
        },
        heroStyle() {
            const image = this.voucher?.avatar?.image_url
                || this.voucher?.avatar?.large
                || this.voucher?.image_url
                || this.voucher?.background_image
                || '/img/member-img/offer-example.svg';
            return {
                backgroundImage: `url(${image})`
            };
        },
        validUntil() {
            const date = this.voucher?.expiration || this.voucher?.expired_at;
            if (!date) return null;
            let dateStr = date;
            if (typeof dateStr === 'string' && dateStr.indexOf('T') === -1 && dateStr.indexOf('Z') === -1 && dateStr.length === 19) {
                dateStr = dateStr.replace(' ', 'T');
            }
            const parsed = dayjs(dateStr);
            if (!parsed.isValid()) return dateStr;
            return parsed.format('DD MMM YYYY, h:mm A');
        },
        location() {
            return this.voucher?.location
                || this.voucher?.merchant?.address
                || this.voucher?.branch?.address
                || null;
        },
        isOnline() {
            return this.$store?.state?.network?.isOnline ?? true;
        }
    },
    watch: {
        voucher: {
            immediate: true,
            handler() {
                this.resetRedeemState();
                this.isDescriptionExpanded = false;
                this.$nextTick(() => this.checkScroll());
            }
        }
    },
    methods: {
        checkScroll() {
            const el = this.$refs.modalCard;
            if (!el) return;
            const isScrollable = el.scrollHeight > el.clientHeight + 15;
            const isNearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 35;
            this.showScrollHint = isScrollable && !isNearBottom;
        },
        scrollToBottom() {
            const el = this.$refs.modalCard;
            if (el) {
                el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
            }
        },
        handleClose() {
            if (this.isRedeeming) return;
            this.$emit('close');
        },
        toggleDescription() {
            this.isDescriptionExpanded = !this.isDescriptionExpanded;
            this.$nextTick(() => this.checkScroll());
        },
        handleRedeem() {
            if (this.isRedeeming) return;
            if (!this.isOnline) return;
            this.isRedeeming = true;
            this.$emit('redeem', this.voucher);
            this.redeemTimeout = setTimeout(() => {
                this.resetRedeemState();
            }, 700);
        },
        resetRedeemState() {
            if (this.redeemTimeout) {
                clearTimeout(this.redeemTimeout);
                this.redeemTimeout = null;
            }
            this.isRedeeming = false;
        }
    }
}

</script>

<style scoped>
.voucher-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(17, 24, 39, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    z-index: 1000;
}

.voucher-modal-card {
    position: relative;
    width: min(480px, 100%);
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 16px 48px rgba(15, 23, 42, 0.18);
    max-height: 90vh;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
}

.voucher-modal-card::-webkit-scrollbar {
    display: none; /* Hide scrollbar Chrome, Safari, Opera */
    width: 0;
    height: 0;
}

.scroll-hint-overlay {
    position: sticky;
    bottom: 92px;
    left: 0;
    right: 0;
    width: 100%;
    height: 55px;
    background: linear-gradient(to top, #ffffff 40%, rgba(255, 255, 255, 0.85) 75%, rgba(255, 255, 255, 0) 100%);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding-bottom: 6px;
    pointer-events: none;
    z-index: 5;
    margin-top: -55px;
    margin-bottom: 0;
    transition: opacity 0.3s ease;
}

.scroll-hint-text {
    pointer-events: auto;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    animation: bounceHintText 2s infinite;
    user-select: none;
    letter-spacing: 0.1px;
}

.scroll-hint-text:hover {
    color: var(--primary-color, #F93E3E);
}

.scroll-hint-icon {
    font-size: 10px;
    color: var(--primary-color, #F93E3E);
}

@keyframes bounceHintText {
    0%, 20%, 50%, 80%, 100% {
        transform: translateY(0);
    }
    40% {
        transform: translateY(3px);
    }
    60% {
        transform: translateY(1.5px);
    }
}

.voucher-modal-close {
    position: absolute;
    top: 18px;
    right: 18px;
    z-index: 2;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 1px solid rgba(209, 213, 219, 0.8);
    background: rgba(255, 255, 255, 0.95);
    color: #0f172a;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s ease;
}

.voucher-modal-close:hover {
    background: rgba(255, 255, 255, 1);
}

.voucher-hero {
    position: sticky;
    top: 0;
    z-index: 1;
    width: 100%;
    flex-shrink: 0;
    aspect-ratio: 16 / 9;
    background-color: #ffffff;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
}

.voucher-point-chip {
    position: absolute;
    bottom: 16px;
    right: 16px;
    background: rgba(255, 255, 255, 0.92);
    color: var(--primary-color, #D91A2A);
    font-weight: 600;
    font-size: 12px;
    padding: 8px 12px;
    border-radius: 999px;
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.1);
}

.voucher-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px;
    background: linear-gradient(180deg, #ffffff 0%, #f9fafb 100%);
}

.voucher-footer {
    position: sticky;
    bottom: 0;
    z-index: 1;
    padding: 16px 24px 24px;
    background: #ffffff;
    border-top: 1px solid #F1F5F9;
}

.voucher-header {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.voucher-title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #111827;
    line-height: 1.35;
}

.voucher-merchant {
    margin: 0;
    font-size: 14px;
    color: #717784;
    line-height: 1.4;
}

.voucher-divider {
    height: 1px;
    width: 100%;
    border-top: 1px dashed #E5E7EB;
    margin: 8px 0;
}

.voucher-meta-row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px 20px;
    background: #F8FAFC;
    border: 1px solid #F1F5F9;
    border-radius: 12px;
    padding: 16px;
}

.meta-col {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1 1 calc(50% - 20px);
    min-width: 140px;
}

.meta-col:not(:last-child) {
    border-right: none;
    margin-right: 0;
}

.meta-icon {
    font-size: 20px;
    color: var(--primary-color, #D91A2A);
}

.meta-text {
    display: flex;
    flex-direction: column;
}

.meta-label {
    font-size: 11px;
    color: #64748B;
    margin-bottom: 2px;
}

.meta-value {
    font-size: 11px;
    font-weight: 600;
    color: #1E293B;
}

.voucher-desc-section {
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    background: #FFFFFF;
    overflow: hidden;
    margin-top: 8px;
}

.desc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    cursor: pointer;
    user-select: none;
}

.desc-title {
    font-size: 16px;
    font-weight: 600;
    color: #1E293B;
}

.desc-toggle-btn {
    background: none;
    border: 1px solid #E2E8F0;
    color: var(--primary-color, #D91A2A);
    border-radius: 8px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.2s;
}

.desc-toggle-btn:hover {
    background-color: #F8FAFC;
}

.desc-content-wrapper {
    padding: 0 16px;
}

.desc-content-wrapper.is-expanded {
    padding-bottom: 16px;
}

.voucher-description-html {
    font-size: 13px;
    color: #475569;
    line-height: 1.6;
}

.voucher-description-snippet {
    font-size: 14px;
    color: #475569;
    padding-bottom: 16px;
    line-height: 1.5;
}

.voucher-description-html :deep(p) {
    margin-bottom: 8px;
}

.voucher-description-html :deep(ul),
.voucher-description-html :deep(ol) {
    margin-bottom: 8px;
    padding-left: 20px;
}

.voucher-description-html :deep(h1),
.voucher-description-html :deep(h2),
.voucher-description-html :deep(h3),
.voucher-description-html :deep(h4),
.voucher-description-html :deep(h5),
.voucher-description-html :deep(h6) {
    margin-top: 12px;
    margin-bottom: 8px;
    font-weight: 600;
    color: #111827;
}

.voucher-description-html :deep(h1) { font-size: 18px; }
.voucher-description-html :deep(h2) { font-size: 16px; }
.voucher-description-html :deep(h3),
.voucher-description-html :deep(h4),
.voucher-description-html :deep(h5),
.voucher-description-html :deep(h6) { font-size: 14px; }

.voucher-description-html :deep(h1:first-child),
.voucher-description-html :deep(h2:first-child),
.voucher-description-html :deep(h3:first-child),
.voucher-description-html :deep(p:first-child) {
    margin-top: 0;
}

.voucher-description-html :deep(p:last-child) {
    margin-bottom: 0;
}

.voucher-description-html :deep(a) {
    color: var(--primary-color, #D91A2A);
    text-decoration: none;
}

.voucher-description-html :deep(a:hover) {
    text-decoration: underline;
}

.voucher-description-html :deep(blockquote) {
    border-left: 3px solid #d1d5db;
    padding-left: 12px;
    margin-left: 0;
    margin-right: 0;
    color: #64748B;
    font-style: italic;
    margin-bottom: 8px;
    background: #F8FAFC;
    padding-top: 4px;
    padding-bottom: 4px;
    border-radius: 0 4px 4px 0;
}

.voucher-eligible-section {
    margin-top: 12px;
    text-align: left;
}

.eligible-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.eligible-title {
    font-size: 13px;
    font-weight: 600;
    color: #1E293B;
}

.eligible-title-icon {
    color: var(--primary-color, #D91A2A);
    margin-right: 4px;
}

.eligible-count-badge {
    font-size: 11px;
    font-weight: 600;
    color: var(--primary-color, #D91A2A);
    background-color: rgba(217, 26, 42, 0.08);
    border: 1px solid rgba(217, 26, 42, 0.15);
    padding: 2px 8px;
    border-radius: 999px;
}

.eligible-list-container {
    padding: 8px;
    border-radius: 8px;
    background-color: #F8FAFC;
    border: 1px solid #E2E8F0;
    max-height: 160px;
    overflow-y: auto;
}

.eligible-merchant-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    border-bottom: 1px solid #F1F5F9;
}

.eligible-merchant-item.is-last {
    border-bottom: none;
}

.merchant-info-group {
    display: flex;
    align-items: center;
    gap: 8px;
}

.location-marker-icon {
    color: #EF4444;
    font-size: 12px;
}

.merchant-name {
    font-size: 12px;
    font-weight: 600;
    color: #0F172A;
}

.branch-name {
    font-size: 11px;
    color: #64748B;
}

.valid-status-badge {
    font-size: 10px;
    font-weight: 600;
    color: #15803D;
    background-color: #DCFCE7;
    border: 1px solid #BBF7D0;
    padding: 2px 6px;
    border-radius: 4px;
}

.voucher-redeem {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: var(--primary-gradient, linear-gradient(90deg, #F97316 0%, #D91A2A 100%));
    color: #ffffff;
    border: none;
    border-radius: 12px;
    padding: 14px 20px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    overflow: hidden;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.voucher-redeem:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.voucher-redeem:disabled,
.voucher-redeem.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #9CA3AF;
}

.voucher-redeem-label {
    position: relative;
    z-index: 2;
}

.voucher-redeem-rip {
    position: absolute;
    width: 32px;
    height: 32px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    top: 50%;
    transform: translateY(-50%) scale(0);
    opacity: 0;
    pointer-events: none;
    z-index: 1;
}

.voucher-redeem-rip--left {
    left: 20px;
}

.voucher-redeem-rip--right {
    right: 20px;
}

@keyframes voucherRipLeft {
    0% { transform: translate(-40%, -50%) scale(0); opacity: 0.4; }
    60% { transform: translate(-120%, -50%) scale(1.2); opacity: 1; }
    100% { transform: translate(-200%, -50%) scale(1.4); opacity: 0; }
}

@keyframes voucherRipRight {
    0% { transform: translate(40%, -50%) scale(0); opacity: 0.4; }
    60% { transform: translate(120%, -50%) scale(1.2); opacity: 1; }
    100% { transform: translate(200%, -50%) scale(1.4); opacity: 0; }
}

.voucher-redeem.is-redeeming .voucher-redeem-rip--left {
    animation: voucherRipLeft 0.7s ease forwards;
}

.voucher-redeem.is-redeeming .voucher-redeem-rip--right {
    animation: voucherRipRight 0.7s ease forwards;
}

@media (max-width: 480px) {
    .voucher-meta-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
    
    .meta-col:not(:last-child) {
        border-right: none;
        margin-right: 0;
        border-bottom: 1px solid #E2E8F0;
        padding-bottom: 16px;
        width: 100%;
    }
}
</style>
